import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, AbstractControl, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Subscription, Subject } from 'rxjs';
import { map, catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from '@env/environment';
import { MessageService } from 'primeng/api';

import { QuoteParametersService } from '../../data/services/view/quote-parameters.service';
import { ApiQuoteParametersService } from '../../data/services/api/api-quote-parameters.service';
import { UserRole } from '@data/enums/user-role.enum';

@Component({
  selector: 'app-quote-form',
  templateUrl: './quote-form.component.html',
  styleUrls: ['./quote-form.component.scss'],
})
export class QuoteFormComponent implements OnInit, OnDestroy {
  entity!: FormGroup;
  paymentTypes: { label: string; value: string; }[] = [
    { label: "Contado", value: "cash" },
    { label: "Financiado", value: "financed" }
  ]
  renewalTypes: { label: string; value: string; }[] = [
    { label: "Contado", value: "cash" },
    { label: "Financiado", value: "financed" }
  ];
  providers: { label: string; value: string; }[] = []
  quoteAmountsSubscription!: Subscription;
  // Búsqueda server-side de clientes con debounce (evita pegarle al API en cada tecla).
  private clientSearch$ = new Subject<string>()
  private clientSearchSubscription!: Subscription;
  quoteAmounts?: any = [{}] as any
  role!: string
  // Expone el enum al template HTML (quote-form.component.html usa UserRole.BROKER)
  public UserRole = UserRole
  assetsValue: number = 0

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    public quoteParametersService: QuoteParametersService,
    private apiQuoteParametersService: ApiQuoteParametersService,
    private messageService: MessageService
  ) { }

  ngOnInit() {
    this.quoteAmountsSubscription = this.quoteParametersService.quoteAmounts$.subscribe(
      (quoteAmounts) => {
        this.quoteAmounts = quoteAmounts;
      }
    );

    // El padre (quote-list o quote-parameters-preview) ya llamó initializeForm() antes de abrir el diálogo.
    // Solo tomamos la referencia al formulario ya inicializado.
    this.entity = this.quoteParametersService.entity;

    // Búsqueda server-side de clientes: cada término escrito en el dropdown consulta al
    // backend (con debounce) para encontrar clientes por nombre O RFC en todo el padrón.
    this.clientSearchSubscription = this.clientSearch$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(term => this.quoteParametersService.searchClients(term));

    if (this.role === UserRole.BROKER) {
      this.entity.get('advance')?.setValue(5);
      this.entity.get('advance')?.setValidators([Validators.required, Validators.min(5)]);
      this.entity.get('advance')?.updateValueAndValidity();

      this.entity.get('interestRate')?.setValue(24);
      this.entity.get('interestRate')?.clearValidators();
      this.entity.get('interestRate')?.updateValueAndValidity();
    }

    this.getProviders().subscribe((response) => {
      if (!response.error) {
        this.providers = response.data.data.map((p: any) => ({ label: p.fullName, value: p._id }));
      } else {
        console.error(response.msg);
      }
    });

    //* Agregar este rol a las validaciones del formulario
    this.role = this.apiQuoteParametersService.user;
  }

  ngOnDestroy() {
    if (this.quoteAmountsSubscription) {
      this.quoteAmountsSubscription.unsubscribe();
    }
    if (this.clientSearchSubscription) {
      this.clientSearchSubscription.unsubscribe();
    }
  }

  /**
   * Handler del filtro del dropdown de cliente. Emite el término al stream con debounce
   * para disparar la búsqueda server-side (nombre O RFC).
   */
  onClientFilter(event: any) {
    this.clientSearch$.next(event?.filter ?? '');
  }

  get e() {
    return this.quoteParametersService.entity
  }

  get accessories(): FormArray {
    return this.entity.get('accessories') as FormArray;
  }

  hasFinancedAccessory(): boolean {
    const accessories = this.quoteParametersService.entity.get('accessories')?.value;
    return accessories ? accessories.some((accessory: any) => accessory.paymentType === 'financed') : false;
  }

  hasCashAccessory(): boolean {
    const accessories = this.quoteParametersService.entity.get('accessories')?.value;
    return accessories ? accessories.some((accessory: any) => accessory.paymentType === 'cash') : false;
  }

  getPaymentType(value: string) {
    return this.paymentTypes.find(pt => pt.value === value)?.label
  }

  getRenewalType(value: string): string {
    const renewalType = this.renewalTypes.find(rt => rt.value === value);
    return renewalType ? renewalType.label : '';
  }

  getProviderName(id: string): string {
    const provider = this.providers.find(p => p.value === id);
    return provider ? provider.label : '';
  }

  addAccessory() {
    const accessoryForm = this.fb.group({
      name: ['', [Validators.required]],
      paymentType: ['', [Validators.required]],
      interestRate: [0],
      amount: [0, [Validators.required]],
      isInsurance: [false],
      insuranceDetails: this.fb.group({
        startPeriod: [],
        endPeriod: [],
        notifyBeforeExpiry: [0],
        provider: [''],
        policyNumber: [''],
        renewalType: [''],
      })
    });

    this.accessories.push(accessoryForm);
  }

  removeAccessory(index: number) {
    this.accessories.removeAt(index);
  }

  addAsset() {
    const assetName = this.quoteParametersService.entity.get('assetInput')?.value;
    if (assetName) {
      const assetsArray = this.quoteParametersService.entity.get('assets')?.value;
      assetsArray.push(assetName);
      this.quoteParametersService.entity.get('assets')?.setValue(assetsArray);
      this.quoteParametersService.entity.get('assetInput')?.setValue('');
    }
  }

  toggleInsuranceDetails(index: number) {
    const accessory = this.accessories.at(index) as FormGroup;
    const isInsurance = accessory.get('isInsurance')?.value;

    if (isInsurance) {
      accessory.get('insuranceDetails')?.enable();
    } else {
      accessory.get('insuranceDetails')?.disable();
    }
  }

  getProviders() {
    return this.http.post(environment.apiUrl + 'providers', { page: 1, limit: 10, filters: {} }).pipe(
      map((r) => r as { error: boolean, msg: string, data: any }),
      catchError((e) => of({ error: true, msg: 'Hubo un error de conexión', data: [] }))
    )
  }

  getRenewalLabel(accessory: AbstractControl): string | undefined {
    const renewalTypeValue = accessory.get('renewalConfig')?.get('renewalType')?.value;
    return this.renewalTypes.find(rt => rt.value === renewalTypeValue)?.label;
  }

  getFormControl(group: FormGroup, controlName: string): FormControl {
    return group.get(controlName) as FormControl;
  }

  onTermsChange(selectedTerms: number[]) {
    const termsArray: any[] = this.quoteParametersService.entity.get('terms')?.value || [];
  
    // Agregar términos nuevos
    selectedTerms.forEach(term => {
      if (!termsArray.some(t => t.term === term)) {
        termsArray.push({ term, residuary: 0 });
      }
    });
  
    // Remover términos deseleccionados
    const updatedTerms = termsArray.filter(t => selectedTerms.includes(t.term));
  
    // Actualizar el control del formulario
    this.quoteParametersService.entity.get('terms')?.setValue(updatedTerms);
  }

  onAssetsChange(event: any) {
    // Solo si el ACTIVO está en modo real (multiselect de activos)
    if (!this.quoteParametersService.useProvisionalAsset) {
      const selectedAssetIds: string[] = event.value || [];
      const calculatedValue = selectedAssetIds.reduce((sum, assetId) => {
        const asset = this.quoteParametersService.actives.find(a => a.value === assetId);
        return sum + (asset?.amount || 0);
      }, 0);
      
      // Usar setTimeout para evitar ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        this.quoteParametersService.entity
          .get('provitionalValue')
          ?.setValue(calculatedValue, { emitEvent: false });
      }, 0);
    }
  }

  /**
   * Maneja el cambio del toggle entre datos reales y provisionales
   */
  onDataModeChange(event: any): void {
    this.quoteParametersService.toggleDataMode(event.checked);

    // Regla de negocio: cliente provisional ⟹ activo provisional (no puede haber activo
    // real con un cliente provisional). Forzamos y bloqueamos el activo a provisional.
    if (event.checked) {
      this.quoteParametersService.useProvisionalAsset = true;
      this.quoteParametersService.updateFormValidations();
    }

    // Solo recalcular si estamos cambiando a modo datos reales Y hay activos cargados
    if (!event.checked && this.quoteParametersService.actives.length > 0) {
      const currentAssets: string[] = this.entity.get('assets')?.value || [];
      this.onAssetsChange({ value: currentAssets });
    } else if (!event.checked && this.quoteParametersService.actives.length === 0) {
      // Intentar recalcular después de un breve delay para que se carguen los activos
      setTimeout(() => {
        if (this.quoteParametersService.actives.length > 0) {
          const currentAssets: string[] = this.entity.get('assets')?.value || [];
          this.onAssetsChange({ value: currentAssets });
        }
      }, 500);
    }

    this.messageService.add({
      severity: 'info',
      summary: 'Modo cambiado',
      detail: event.checked
        ? 'Ahora puedes ingresar datos provisionales'
        : 'Ahora debes seleccionar datos reales de la base de datos'
    });
  }

  /**
   * Maneja el cambio del toggle del ACTIVO (real vs provisional), independiente del cliente.
   */
  onAssetModeChange(event: any): void {
    this.quoteParametersService.toggleAssetMode(event.checked);

    // Al pasar a activo real, recalcular el valor desde los activos seleccionados.
    if (!event.checked && this.quoteParametersService.actives.length > 0) {
      const currentAssets: string[] = this.entity.get('assets')?.value || [];
      this.onAssetsChange({ value: currentAssets });
    }
  }

  /**
   * Formatea la fecha para mostrar en el campo de solo lectura
   */
  formatDisplayDate(dateValue: string): string {
    if (!dateValue) return 'Fecha no disponible';
    
    const date = new Date(dateValue);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
}
